import React, { useState } from 'react';
import {
  Switch,
  Button,
  Chip,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Stack
} from '@mui/material';
import {
  Save,
  ExpandMore,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import Layout from '../layout/layout';
import PropTypes from 'prop-types';

/* =========================================================
   MOCK CONFIG (UNCHANGED)
   ========================================================= */

const mockConfig = [
  {
    provider: 'AWS',
    category: 'compute',
    fields: [
      { id: 'instanceName', name: 'Instance Name', type: 'input', required: true, enabled: true },
      { id: 'instanceType', name: 'Instance Type', type: 'dropdown', required: true, enabled: true, options: ['t3.micro', 't3.small', 't3.medium', 't3.large'] },
      { id: 'region', name: 'Region', type: 'dropdown', required: true, enabled: true, options: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'] },
      { id: 'keyPair', name: 'Key Pair', type: 'dropdown', required: false, enabled: true, options: ['default-key', 'prod-key', 'dev-key'] },
      { id: 'securityGroup', name: 'Security Group', type: 'dropdown', required: true, enabled: false, options: ['default', 'web-sg', 'app-sg'] },
      { id: 'userData', name: 'User Data', type: 'textarea', required: false, enabled: true },
      { id: 'monitoring', name: 'Enable Monitoring', type: 'checkbox', required: false, enabled: true },
      { id: 'autoScaling', name: 'Enable Auto Scaling', type: 'checkbox', required: false, enabled: false },
    ]
  },
  {
    provider: 'AWS',
    category: 'storage',
    fields: [
      { id: 'bucketName', name: 'Bucket Name', type: 'input', required: true, enabled: true },
      { id: 'region', name: 'Region', type: 'dropdown', required: true, enabled: true, options: ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1'] },
      { id: 'storageClass', name: 'Storage Class', type: 'dropdown', required: false, enabled: true, options: ['Standard', 'IA', 'Glacier', 'Deep Archive'] },
      { id: 'versioning', name: 'Enable Versioning', type: 'checkbox', required: false, enabled: false },
      { id: 'encryption', name: 'Enable Encryption', type: 'checkbox', required: false, enabled: true },
    ]
  }
];

/* =========================================================
   🔧 HELPERS (EXTRACTED TO FIX NESTING)
   ========================================================= */

const matchesSection = (resource, providerCategory) =>
  `${resource.provider}-${resource.category}` === providerCategory;

const toggleFieldInResource = (resource, providerCategory, fieldId) => {
  if (!matchesSection(resource, providerCategory)) return resource;

  return {
    ...resource,
    fields: resource.fields.map(field =>
      field.id === fieldId
        ? { ...field, enabled: !field.enabled }
        : field
    )
  };
};

const toggleRequiredInResource = (resource, providerCategory, fieldId) => {
  if (!matchesSection(resource, providerCategory)) return resource;

  return {
    ...resource,
    fields: resource.fields.map(field =>
      field.id === fieldId
        ? { ...field, required: !field.required }
        : field
    )
  };
};

const setAllFieldsEnabled = (resource, providerCategory, enabled) => {
  if (!matchesSection(resource, providerCategory)) return resource;

  return {
    ...resource,
    fields: resource.fields.map(field => ({ ...field, enabled }))
  };
};

const getProviderColor = (provider) => {
  switch (provider.toLowerCase()) {
    case 'aws': return '#FF9900';
    case 'azure': return '#0078D4';
    case 'gcp': return '#4285F4';
    default: return '#1976d2';
  }
};

/* =========================================================
   COMPONENT
   ========================================================= */

export function AdminFieldConfig({ onSave }) {
  const [config, setConfig] = useState(mockConfig);
  const [expandedSections, setExpandedSections] = useState(new Set(['AWS-compute']));
  const [searchTerm] = useState('');
  const [filterProvider] = useState('all');
  const [filterCategory] = useState('all');

  /* ================== TOGGLES ================== */

  const toggleField = (providerCategory, fieldId) => {
    setConfig(prev =>
      prev.map(resource =>
        toggleFieldInResource(resource, providerCategory, fieldId)
      )
    );
  };

  const toggleFieldRequired = (providerCategory, fieldId) => {
    setConfig(prev =>
      prev.map(resource =>
        toggleRequiredInResource(resource, providerCategory, fieldId)
      )
    );
  };

  const enableAllFields = (providerCategory) => {
    setConfig(prev =>
      prev.map(resource =>
        setAllFieldsEnabled(resource, providerCategory, true)
      )
    );
  };

  const disableAllFields = (providerCategory) => {
    setConfig(prev =>
      prev.map(resource =>
        setAllFieldsEnabled(resource, providerCategory, false)
      )
    );
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  };

  const handleSave = () => {
    onSave(config);
  };

  /* ================== FILTERING ================== */

  // ========== EXTRACTED HELPER FUNCTIONS TO REDUCE NESTING ==========
  
  const checkSearchMatch = (resource, searchTerm) => {
    if (!searchTerm) return true;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    const providerMatch = resource.provider.toLowerCase().includes(lowerSearchTerm);
    const categoryMatch = resource.category.toLowerCase().includes(lowerSearchTerm);
    const fieldMatch = resource.fields.some(field =>
      field.name.toLowerCase().includes(lowerSearchTerm)
    );
    
    return providerMatch || categoryMatch || fieldMatch;
  };

  const checkProviderMatch = (resource, filterProvider) => {
    return filterProvider === 'all' || resource.provider === filterProvider;
  };

  const checkCategoryMatch = (resource, filterCategory) => {
    return filterCategory === 'all' || resource.category === filterCategory;
  };

  const matchesAllFilters = (resource) => {
    return (
      checkSearchMatch(resource, searchTerm) &&
      checkProviderMatch(resource, filterProvider) &&
      checkCategoryMatch(resource, filterCategory)
    );
  };

  const filteredConfig = config.filter(matchesAllFilters);

  /* ================== RENDER ================== */

  return (
    <Layout pageType="sideBar">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4">Field Configuration</Typography>
          <Button variant="contained" color="success" startIcon={<Save />} onClick={() => handleSave()}>
            Save Configuration
          </Button>
        </Box>

        <Stack spacing={2}>
          {filteredConfig.map(resource => {
            const sectionId = `${resource.provider}-${resource.category}`;
            const isExpanded = expandedSections.has(sectionId);

            return (
              <Accordion
                key={sectionId}
                expanded={isExpanded}
                onChange={() => toggleSection(sectionId)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Chip
                    label={resource.provider}
                    sx={{
                      bgcolor: `${getProviderColor(resource.provider)}20`,
                      color: getProviderColor(resource.provider),
                      mr: 2
                    }}
                  />
                  <Typography sx={{ textTransform: 'capitalize' }}>
                    {resource.category}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => enableAllFields(sectionId)} startIcon={<Visibility />}>
                        Enable All
                      </Button>
                      <Button size="small" onClick={() => disableAllFields(sectionId)} startIcon={<VisibilityOff />}>
                        Disable All
                      </Button>
                    </Stack>

                    {resource.fields.map(field => (
                      <Paper key={field.id} sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Switch
                            checked={field.enabled}
                            onChange={() => toggleField(sectionId, field.id)}
                          />
                          <Typography
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleFieldRequired(sectionId, field.id)}
                          >
                            {field.name}
                          </Typography>
                          {field.required && <Chip label="Required" size="small" color="error" />}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Box>
    </Layout>
  );
}

AdminFieldConfig.propTypes = {
  onSave: PropTypes.func.isRequired 
};